import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAppSession } from "../lib/shell";
import { useTimeClockEligibility } from "../lib/time-clock-query";

export function safeClockReturn(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") && !value.includes("\\") && ![...value].some((character) => character.charCodeAt(0) < 32)
    && value.split(/[?#]/)[0] !== "/time-clock" ? value : "/work-orders";
}

/** Attendance is checked against the server's fleet day, never the browser's date. */
export function DailyClockGate({ children }: { children: ReactNode }) {
  const { realRoles } = useAppSession();
  const mechanic = realRoles.includes("mechanic") && !realRoles.some((role) => role === "admin" || role === "manager");
  const location = useLocation();
  const eligibility = useTimeClockEligibility(mechanic);
  const [expiredAt, setExpiredAt] = useState(0);
  const { data, dataUpdatedAt, refetch } = eligibility;
  const expiresAt = data ? dataUpdatedAt + Date.parse(data.nextFleetDayAt) - Date.parse(data.serverTime) : 0;
  const fresh = Number.isFinite(expiresAt) && Date.now() < expiresAt && expiredAt !== dataUpdatedAt;
  useEffect(() => {
    if (!mechanic || !data) return;
    const delay = Math.max(0, expiresAt - Date.now());
    const timer = window.setTimeout(() => { setExpiredAt(dataUpdatedAt); void refetch(); }, delay);
    return () => window.clearTimeout(timer);
  }, [mechanic, data, dataUpdatedAt, expiresAt, refetch]);
  if (!mechanic) return <>{children}</>;
  const clockPage = location.pathname === "/time-clock";
  // Account remedies and signing out stay reachable if attendance cannot load.
  if (location.pathname === "/settings" || location.pathname.startsWith("/settings/")) return <>{children}</>;
  if (clockPage) return <>
    {data?.requiresClockIn && <p className="screen-body" role="status">Start your day by punching in. If your location cannot be verified, request manager approval below.</p>}
    {data && !data.requiresClockIn && !eligibility.isError && fresh && <p className="screen-body" role="status">Your daily clock-in is recorded. <Link className="btn btn-primary" to={safeClockReturn(new URLSearchParams(location.search).get("returnTo"))}>Continue to work</Link></p>}
    {children}
  </>;
  if (!data || eligibility.isError || !fresh) return <div className="screen-body" role="status">
    <p>{eligibility.isError ? "Could not check today’s clock-in. Retry or open your time clock." : "Checking today’s clock-in…"}</p>
    {eligibility.isError && <><button className="btn" onClick={() => { void refetch(); }}>Retry clock-in check</button> <Link to="/time-clock">Open time clock</Link></>}
  </div>;
  if (data.requiresClockIn) return <Navigate replace to={`/time-clock?returnTo=${encodeURIComponent(location.pathname + location.search + location.hash)}`} />;
  return <>{children}</>;
}
